import React, { useEffect, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, Wheat, Milk, Soup, Cookie, CupSoda, Home, Package, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/state/AppContext";
import { useRequireAuth } from "@/lib/useRequireAuth";
import { api, apiErrorMessage, rows } from "@/lib/api";
import { localized } from "@/theme/tenantTheme";
import { resolveImage } from "@/lib/media";
import { rupeesSmart } from "@/lib/format";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/** A veg/non-veg dot, `catalog_items.is_veg` — `null` (unset) renders nothing, never a fabricated
 * default. Matches the handoff's bordered-square-with-centre-dot mark. */
function VegDot({ isVeg, testId }) {
  if (isVeg == null) return null;
  const color = isVeg ? "border-green-600" : "border-red-600";
  const fill = isVeg ? "bg-green-600" : "bg-red-600";
  return (
    <span
      data-testid={testId}
      className={`inline-flex h-3 w-3 shrink-0 items-center justify-center rounded-[2px] border ${color}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${fill}`} />
    </span>
  );
}

//: `catalog_items.section` -> a category tile icon (kirana category grid, C2). Keyword match on
//: the lowercased section name — no icon for a section that matches nothing, never a wrong icon.
const CATEGORY_ICONS = [
  [/atta|rice|staple|grain/, Wheat],
  [/dairy|milk/, Milk],
  [/oil|masala|spice/, Soup],
  [/snack/, Cookie],
  [/beverage|drink/, CupSoda],
  [/household/, Home],
];

function categoryIcon(section) {
  const s = String(section || "").toLowerCase();
  const match = CATEGORY_ICONS.find(([re]) => re.test(s));
  return match ? match[1] : Package;
}

/** This item's live quantity in the visitor's cart (no size option — grocery items never carry
 * options), or 0. Reuses the SAME `cart` state `Cart.jsx` reads — no parallel cart tracking. */
function cartQtyFor(cart, businessId, item) {
  if (!cart || cart.business_id !== businessId) return 0;
  const line = cart.items.find((l) => l.item_id === item.id && !l.option_name);
  return line ? { line, quantity: line.quantity } : 0;
}

// D2 (storefront S3): a `stay_booking` business (a hotel) that also takes `ordering` runs its
// "Dining" section open to non-guests, per the handoff — the mode toggle (home delivery /
// takeaway / to-my-room) is shown here, ABOVE the menu, before anything is added to the cart,
// not only at checkout (`Cart.jsx` already had room_service wired).
const HOTEL_DINING_MODES = ["delivery", "pickup", "room_service"];

export function MenuSection({ business }) {
  const { t, lang, auth, cart, refreshCart, setFulfillmentPrefill } = useApp();
  const caps = business.capabilities || [];
  const isHotelDining = caps.includes("stay_booking") && caps.includes("ordering");
  const [diningMode, setDiningMode] = useState("delivery");
  const [roomNumber, setRoomNumber] = useState("");
  const chooseDiningMode = (mode) => {
    setDiningMode(mode);
    setFulfillmentPrefill({ fulfillment_type: mode, room_number: mode === "room_service" ? roomNumber : "" });
  };
  const navigate = useNavigate();
  const requireAuth = useRequireAuth();
  const [items, setItems] = useState([]);
  // U9: loaded-empty ("no menu items yet") is a different state from still-loading.
  const [loaded, setLoaded] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api
      .get(`/businesses/${business.id}/catalog`)
      .then((r) => setItems(rows(r.data)))
      .finally(() => setLoaded(true));
  }, [business.id]);

  const add = async (item, optionName = "") => {
    // One up-front convention (L2): the shared sign-in prompt, which returns here after login.
    if (!requireAuth()) return;
    try {
      await api.post("/cart/items", {
        business_id: business.id,
        item_id: item.id,
        option_name: optionName,
        quantity: 1,
      });
      await refreshCart();
      const label = optionName ? `${localized(lang, item, "name")} · ${optionName}` : localized(lang, item, "name");
      toast.success(`${label} · ${t("commerce.inCart")}`);
    } catch (e) {
      // Backend sends the shop name in `error.details` (see commerce/service.py's Conflict
      // raise), not packed into the message string the way the old Mongo `cart_conflict:{name}`
      // format did — this was still checking for that retired string shape, so the conflict
      // dialog never fired and every cross-shop add just showed a generic toast instead.
      const details = e?.response?.data?.error?.details;
      if (e?.response?.status === 409 && details?.reason === "cart_conflict") {
        setConflict({ item, optionName, business: details.business_name || "" });
        return;
      }
      toast.error(apiErrorMessage(e, t("errors.generic")));
    }
  };

  const setQty = async (cartItemId, quantity) => {
    try {
      await api.patch(`/cart/items/${cartItemId}`, { quantity });
      await refreshCart();
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    }
  };

  const clearAndAdd = async () => {
    const { item, optionName } = conflict;
    setConflict(null);
    await api.post("/cart/clear");
    await add(item, optionName);
  };

  const isGrocery = business.business_type === "grocery";
  const hasVeg = items.some((i) => i.is_veg === true);
  const filteredItems = filter === "veg" ? items.filter((i) => i.is_veg === true) : items;
  const sections = [...new Set(filteredItems.map((i) => i.section))];
  // The combo/special highlight (C1): the FIRST owner-featured item, from the unfiltered list —
  // a veg filter should not hide the highlight card itself, only the rows below it. Never shown
  // for the grocery grid layout, which has no equivalent in the Kirana handoff.
  const featured = !isGrocery ? items.find((i) => i.is_featured) : null;

  const sectionLabel = (section) => {
    if (lang !== "hi") return section;
    const match = items.find((i) => i.section === section && i.section_hi);
    if (match) return match.section_hi;
    // R3-9: "General" is the platform's own default section name (not owner data), so it has a
    // translation; an owner-named section with no `section_hi` stays as typed.
    return section === "General" ? t("business.sectionGeneral") : section;
  };

  const priceLabel = (item) => {
    if (!item.options || item.options.length === 0) return rupeesSmart(item.price_minor_units);
    const prices = item.options.map((o) => o.price_minor_units);
    return `${rupeesSmart(Math.min(...prices))} – ${rupeesSmart(Math.max(...prices))}`;
  };

  const addOrStepper = (item) => {
    const found = cartQtyFor(cart, business.id, item);
    if (!found) {
      return (
        <Button
          size="sm"
          className="rounded-full"
          data-testid={`add-to-cart-${item.id}`}
          onClick={() => add(item)}
        >
          <Plus size={14} className="mr-1" /> {t("commerce.addToCart")}
        </Button>
      );
    }
    return (
      <div className="flex items-center gap-2" data-testid={`item-qty-stepper-${item.id}`}>
        <Button
          size="icon"
          variant="outline"
          className="h-7 w-7 rounded-full"
          data-testid={`item-qty-decrease-${item.id}`}
          onClick={() => setQty(found.line.id, found.quantity - 1)}
        >
          <Minus size={12} />
        </Button>
        <span className="w-4 text-center tabular-nums text-sm" data-testid={`item-qty-value-${item.id}`}>
          {found.quantity}
        </span>
        <Button
          size="icon"
          variant="outline"
          className="h-7 w-7 rounded-full"
          data-testid={`item-qty-increase-${item.id}`}
          onClick={() => setQty(found.line.id, found.quantity + 1)}
        >
          <Plus size={12} />
        </Button>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="menu-section">
      {isHotelDining && (
        <div className="space-y-2" data-testid="hotel-dining-mode">
          <div className="flex flex-wrap gap-2">
            {HOTEL_DINING_MODES.map((mode) => (
              <button
                key={mode}
                type="button"
                data-testid={`dining-mode-${mode}`}
                onClick={() => chooseDiningMode(mode)}
                className={`rounded-full border px-4 py-1.5 text-sm ${
                  diningMode === mode
                    ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground"
                    : "border-border"
                }`}
              >
                {mode === "delivery" && t("commerce.deliveryMode")}
                {mode === "pickup" && t("commerce.pickupMode")}
                {mode === "room_service" && t("commerce.roomServiceMode")}
              </button>
            ))}
          </div>
          {diningMode === "room_service" && (
            <input
              type="text"
              data-testid="dining-room-number"
              placeholder={t("commerce.roomNumberPlaceholder")}
              value={roomNumber}
              onChange={(e) => {
                setRoomNumber(e.target.value);
                setFulfillmentPrefill({ fulfillment_type: "room_service", room_number: e.target.value });
              }}
              className="w-full max-w-xs rounded-xl border border-border px-3 py-2 text-sm"
            />
          )}
          <p className="flex items-center gap-1.5 text-xs font-semibold text-[hsl(var(--primary))]">
            <CheckCircle2 size={14} /> {t("commerce.diningOpenToAll")}
          </p>
        </div>
      )}
      {!loaded && (
        <div className="space-y-3" data-testid="menu-loading">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-2xl" />
          ))}
        </div>
      )}
      {loaded && items.length === 0 && <p className="text-muted-foreground">{t("commerce.noMenuItems")}</p>}

      {items.length > 0 && (
        <>
          {featured && (
            <div
              className="flex flex-wrap items-center gap-4 rounded-2xl border border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/10 p-5"
              data-testid="menu-featured-highlight"
            >
              <div className="min-w-[200px] flex-1">
                <p className="text-[11px] font-semibold uppercase tracking-widest text-[hsl(var(--accent-ink))]">
                  {t("commerce.todaysSpecial")}
                </p>
                <p className="mt-1.5 font-heading text-lg font-semibold">{localized(lang, featured, "name")}</p>
                {featured.description && (
                  <p className="mt-1 text-sm text-muted-foreground">{localized(lang, featured, "description")}</p>
                )}
              </div>
              <div className="text-right">
                <p className="tabular-nums text-xl font-bold">{priceLabel(featured)}</p>
                {featured.is_available !== false && (
                  <Button
                    size="sm"
                    className="mt-2 rounded-full"
                    data-testid={`add-to-cart-${featured.id}`}
                    onClick={() => add(featured)}
                  >
                    {t("commerce.addToOrder")}
                  </Button>
                )}
              </div>
            </div>
          )}

          {(hasVeg || isGrocery) && (
            <div className="flex flex-wrap gap-2" data-testid="menu-filter-chips">
              <button
                type="button"
                data-testid="menu-filter-all"
                onClick={() => setFilter("all")}
                className={`rounded-full px-3.5 py-1.5 text-xs font-semibold ${
                  filter === "all"
                    ? "bg-[hsl(var(--primary))] text-white"
                    : "border border-border bg-card text-foreground"
                }`}
              >
                {t("commerce.menuFilterAll")}
              </button>
              {hasVeg && (
                <button
                  type="button"
                  data-testid="menu-filter-veg"
                  onClick={() => setFilter("veg")}
                  className={`rounded-full px-3.5 py-1.5 text-xs font-semibold ${
                    filter === "veg"
                      ? "bg-[hsl(var(--primary))] text-white"
                      : "border border-border bg-card text-foreground"
                  }`}
                >
                  {t("commerce.menuFilterVeg")}
                </button>
              )}
            </div>
          )}

          {isGrocery ? (
            <>
              {/* C2 — category tile grid */}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="catalog-category-grid">
                {sections.map((section) => {
                  const Icon = categoryIcon(section);
                  return (
                    <a
                      key={section}
                      href={`#catalog-section-${section}`}
                      data-testid={`catalog-category-tile-${section}`}
                      className="flex flex-col items-center gap-2 rounded-2xl border border-border bg-card p-4 text-center"
                    >
                      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]">
                        <Icon size={22} />
                      </span>
                      <span className="text-xs font-semibold">{sectionLabel(section)}</span>
                    </a>
                  );
                })}
              </div>

              {/* C2 — product grid, grouped so a section anchor still lands somewhere sensible */}
              {sections.map((section) => (
                <div key={section} id={`catalog-section-${section}`}>
                  <h3 className="font-heading text-lg font-semibold">{sectionLabel(section)}</h3>
                  <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid={`catalog-product-grid-${section}`}>
                    {filteredItems
                      .filter((i) => i.section === section)
                      .map((item) => (
                        <div
                          key={item.id}
                          data-testid={`menu-item-${item.id}`}
                          className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card"
                        >
                          {item.image_url && (
                            <img
                              src={resolveImage(item.image_url)}
                              alt={localized(lang, item, "name")}
                              width={400}
                              height={400}
                              loading="lazy" decoding="async"
                              className="aspect-square w-full object-cover"
                            />
                          )}
                          <div className="flex flex-1 flex-col gap-1.5 p-3">
                            <p className="flex items-center gap-1.5 text-sm font-semibold leading-tight">
                              <VegDot isVeg={item.is_veg} testId={`menu-item-veg-${item.id}`} />
                              {localized(lang, item, "name")}
                            </p>
                            {item.description && (
                              <p className="text-xs text-muted-foreground">{localized(lang, item, "description")}</p>
                            )}
                            <div className="mt-auto flex items-center justify-between gap-2 pt-1.5">
                              <span className="tabular-nums text-sm font-bold">{priceLabel(item)}</span>
                              {item.is_available !== false ? addOrStepper(item) : (
                                <span className="text-xs text-muted-foreground">{t("commerce.unavailable")}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </>
          ) : (
            // C1 — always-visible inline rows, grouped under category headings.
            sections.map((section) => (
              <div key={section}>
                <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  {sectionLabel(section)}
                </h3>
                <div className="mt-3 divide-y divide-border overflow-hidden rounded-2xl border border-border">
                  {filteredItems
                    .filter((i) => i.section === section)
                    .map((item) => {
                      const options = item.options || [];
                      return (
                        <div key={item.id} className="p-4" data-testid={`menu-item-${item.id}`}>
                          <div className="flex items-center gap-4">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <VegDot isVeg={item.is_veg} testId={`menu-item-veg-${item.id}`} />
                                <span className="font-medium">{localized(lang, item, "name")}</span>
                              </div>
                              {item.description && (
                                <p className="mt-1 max-w-[44ch] text-sm text-muted-foreground">
                                  {localized(lang, item, "description")}
                                </p>
                              )}
                              <p className="mt-1.5 tabular-nums text-sm font-semibold">{priceLabel(item)}</p>
                              {options.length > 0 && (
                                <p className="mt-0.5 text-xs text-[hsl(var(--primary))]">
                                  {t("commerce.sizesAvailable", { count: options.length })}
                                </p>
                              )}
                            </div>
                            {item.image_url ? (
                              // R4-10: the Add / stepper control sits BELOW the photo, not over it - on an
                              // 80px thumbnail the pill covered most of the dish.
                              <div className="flex shrink-0 flex-col items-center gap-2">
                                <div className="h-20 w-20 overflow-hidden rounded-xl">
                                  <img
                                    src={resolveImage(item.image_url)}
                                    alt={localized(lang, item, "name")}
                                    width={80}
                                    height={80}
                                    loading="lazy" decoding="async"
                                    className="h-full w-full object-cover"
                                  />
                                </div>
                                {options.length === 0 && item.is_available !== false && addOrStepper(item)}
                              </div>
                            ) : (
                              options.length === 0 &&
                              (item.is_available !== false ? (
                                // Reflects what is in the cart (a stepper once added), like the
                                // grocery grid — a static "Add" gave no sign the add had landed (L8).
                                <div className="shrink-0">{addOrStepper(item)}</div>
                              ) : (
                                <span className="shrink-0 text-xs text-muted-foreground">{t("commerce.unavailable")}</span>
                              ))
                            )}
                          </div>

                          {options.length > 0 && item.is_available !== false && (
                            <div className="mt-3 space-y-2">
                              {options.map((option) => (
                                <div
                                  key={option.name}
                                  className="flex items-center justify-between gap-4 rounded-xl border border-border p-3"
                                  data-testid={`menu-option-${item.id}-${option.name.replace(/\s+/g, "-").toLowerCase()}`}
                                >
                                  <div>
                                    <p className="text-sm font-medium">
                                      {lang === "hi" ? option.name_hi || option.name : option.name}
                                    </p>
                                    <p className="tabular-nums text-sm text-muted-foreground">
                                      {rupeesSmart(option.price_minor_units)}
                                    </p>
                                  </div>
                                  <Button
                                    size="sm"
                                    className="rounded-full"
                                    data-testid={`add-option-${item.id}-${option.name.replace(/\s+/g, "-").toLowerCase()}`}
                                    onClick={() => add(item, option.name)}
                                  >
                                    <Plus size={14} className="mr-1" /> {t("commerce.addToCart")}
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            ))
          )}
        </>
      )}

      {cart && cart.business_id === business.id && (
        <Button className="rounded-full" data-testid="go-to-cart-btn" onClick={() => navigate("/cart")}>
          {t("commerce.cart")} · {cart.items.length}
        </Button>
      )}

      <AlertDialog open={!!conflict} onOpenChange={(open) => !open && setConflict(null)}>
        <AlertDialogContent data-testid="cart-conflict-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("commerce.conflictTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("commerce.conflictBody", { business: conflict?.business || "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cart-conflict-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="cart-conflict-clear" onClick={clearAndAdd}>
              {t("commerce.clearAndAdd")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
